import React from 'react';
import { useTranslation } from 'react-i18next';
import { useMonolithFrontendUrl } from '@/shared/hooks/useMonolithFrontendUrl.ts';
import { getProcessingTypeFromEntity } from '@/shared/utils/getProcessingTypeFromEntity.ts';
import { PageHeader } from '@/shared/components/PageHeader.tsx';
import { ThumbsUpImage } from '@/modules/order/components/OrderConfirmation/components/ThumbsUpImage.tsx';
import { Link } from 'react-router';
import { Button } from '@schuettflix/planum-react';
import { GetOrderResponse } from '@schuettflix/interfaces';

export function OrderConfirmationPage({ order }: { order: GetOrderResponse }) {
    const { t } = useTranslation();
    const { url: dashboardUrl } = useMonolithFrontendUrl({
        path: '/dashboard',
        target: 'currentTab',
    });

    const processingType = getProcessingTypeFromEntity(order);

    return (
        <div className="bg-light-gray-200 min-h-screen">
            <PageHeader title={t('page.title')} onCloseClickPath={dashboardUrl} />
            <div className="translate-y-1/3 p-6 text-center">
                <div className="flex flex-col gap-8">
                    <figure className="flex justify-center">
                        <ThumbsUpImage />
                    </figure>

                    <div className="flex flex-col gap-2">
                        <h1 className="font-headline-xl-strong">
                            {t('sections.summary.buttons.orderPlaced', {
                                orderHumanReadableId: order.humanReadableId,
                            })}
                        </h1>
                        <p className="font-copy-md text-center">
                            {t('sections.summary.orderPlaced.description.orderConfirmation')}
                            {processingType === 'MANUAL' && (
                                <span className="block">
                                    <Link
                                        to={`/order/${order.id}/fulfillment`}
                                        className="underline"
                                        data-test="fulfillment-link"
                                    >
                                        {t('sections.summary.orderPlaced.description.navigationAction_v2')}
                                    </Link>
                                    ,&nbsp;{t('sections.summary.orderPlaced.description.viewOrder_v2')}
                                </span>
                            )}
                        </p>
                    </div>

                    <div className="flex flex-col gap-2 md:flex-row md:justify-center">
                        <Link to={dashboardUrl}>
                            <Button className="w-full md:w-auto" variant="secondary">
                                {t('sections.summary.buttons.overviewPage')}
                            </Button>
                        </Link>
                        {order.positionId ? (
                            <Link to={`/position/${order.positionId}/order`}>
                                <Button>{t('sections.summary.buttons.newPositionOrder')}</Button>
                            </Link>
                        ) : (
                            <Link to="/order" className="w-full md:w-auto">
                                <Button variant="primary">{t('sections.summary.buttons.newOrder')}</Button>
                            </Link>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
